"use client";

import { TextButton } from "@rosette/web_components/src/ui/TextButton.js";
import { useSingleton } from "@rosette/web_utils/src/useSingleton.js";
import type { GameClientControls } from "@rosette/ts/src/business/GameClientControls.js";
import { useGamePreferences } from "@/app/(api)/api/getGamePreferences/useGamePreferences.js";
import { useOptionalUser } from "@/components/env/EnvContext.js";
import { matomo } from "@/components/Matomo.js";
import { useGameAPI } from "@/components/api/GameAPIContext.js";
import { DeviceInfoProvider, useIsMobile } from "@rosette/web_components/src/accessibility/DeviceInfoContext.js";
import { AssetProvider, useAssetLoader } from "@/components/assets/AssetContext.js";
import { GameUIContext, usePerfectGameController } from "@/components/game/GameUIContext.js";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import BoardUI from "@/components/game/board/BoardUI.js";
import { useCreateSharedRendererState } from "@rosette/web_components/src/game/SharedRendererState.js";
import { cn } from "@rosette/ts/src/util/cn.js";
import { PerfectGameSource } from "@rosette/ts/src/business/game/controller/source/PerfectGameSource.js";
import { useDerivedRune, useRune } from "@rosette/react_utils/src/useRune.js";
import { PerfectGameMetadata } from "@/components/game/PerfectGameMetadata.js";
import { Button } from "@rosette/web_components/src/ui/Button.js";
import { AnalysisSnakeGraph } from "@/components/game/analysis/AnalysisSnakeGraph.js";
import { PlayerType } from "@rosette/ts/src/royalur/model/PlayerType.js";
import { MoveInfo } from "@rosette/ts/src/business/analysis/MoveInfo.js";
import { AudioSettings } from "@rosette/web_components/src/assets/AudioSettings.js";
import { Optional } from "@rosette/ts/src/util/Optional.js";
import { DotDotDot } from "@rosette/web_components/src/game/timing/DotDotDot.js";


type Menu = "start" | "finish" | "loading" | "error";


function getLWP(move: MoveInfo): number | null {
    if (!move.moveState)
        return null;

    const lwpStr = move.moveState.getMetadata()["lwp"];
    if (lwpStr !== undefined)
        return parseFloat(lwpStr);

    return null;
}

function getLWPs(moves: MoveInfo[], winner: PlayerType): number[] {
    const lwps: number[] = [];
    let lastLWP: number = 50;
    for (const move of moves) {
        const lwp = getLWP(move);
        if (lwp !== null) {
            lastLWP = lwp;
            break;
        }
    }
    for (const move of moves) {
        const lwp = getLWP(move) ?? lastLWP;
        lastLWP = lwp;
        lwps.push(lwp);
    }
    lwps.push(winner === PlayerType.LIGHT ? 100.0 : 0.0);
    return lwps;
}


interface PerfectGameControlProps {
    children: string;
    onClick: () => void;
}


function PerfectGameControl({ children, onClick }: PerfectGameControlProps) {
    return (
        <TextButton
            tone="muted"
            className="text-base"
            onClick={onClick}>

            {children}
        </TextButton>
    );
}


interface InternalPerfectGameProps {
    className?: string;
    gameSource: PerfectGameSource;
}


function InternalPerfectGame({ className, gameSource }: InternalPerfectGameProps) {
    const isMobile = useIsMobile();
    const sharedRendererState = useCreateSharedRendererState();

    // Audio Controls.
    const assetLoader = useAssetLoader();
    const muted = (useRune(assetLoader.getAudioSettings()) === AudioSettings.MUTED);
    useEffect(() => {
        assetLoader.getAudioSettings().set(AudioSettings.QUIET);
    }, [assetLoader]);

    const toggleMuted = useCallback(() => {
        assetLoader.getAudioSettings().set(muted ? AudioSettings.QUIET : AudioSettings.MUTED);
    }, [assetLoader, muted]);

    // Link the game controller.
    const gameController = usePerfectGameController();
    useEffect(() => {
        return gameSource.linkGameController(gameController);
    }, [gameSource, gameController]);

    const skip = useCallback(() => {
        gameController.skip();
    }, [gameController]);

    // Get information about the game that is currently being shown.
    const game = useRune(gameSource.currentGame).getOrNull();
    const winner: PlayerType | undefined = (game ? game.getWinner() : undefined);
    const moves: MoveInfo[] | undefined = useMemo(
        () => game ? MoveInfo.listMoves(game) : undefined,
        [game],
    );
    const lwps: number[] | undefined = useMemo(() => {
        return moves && winner ? getLWPs(moves, winner) : undefined;
    }, [moves, winner]);

    // Determine whether the game is finished animating.
    const moveCount = moves?.length;
    const isGameFinishedFn = useCallback((moveIndex: number) => {
        return !!moveCount && moveIndex + 1 >= moveCount;
    }, [moveCount]);
    const isGameFinished = useDerivedRune(sharedRendererState.currentMoveIndex, isGameFinishedFn);

    const [animating, setAnimating] = useState(false);
    useEffect(() => {
        return gameController.subscribeToActiveDirective((directive) => {
            setAnimating(!directive.isLimboDirective());
        });
    }, [gameController, setAnimating]);

    // Update the mode based upon
    useEffect(() => {
        return sharedRendererState.currentMoveIndex.subscribe((moveIndex: number) => {
            const playing = moveIndex > 0;
            const finished = !!moveCount && moveIndex + 1 >= moveCount;
            if (playing && !finished) {
                gameSource.mode.set("play");
            }
        });
    }, [sharedRendererState, moveCount, gameSource]);

    // Determine what menu to show.
    const error = useRune(gameSource.error);
    const mode = useRune(gameSource.mode);

    let menuToShow: Menu | null = null;
    if (mode === "idle") {
        menuToShow = (isGameFinished ? "finish" : "start");
    } else if (mode === "play") {
        menuToShow = (!isGameFinished || animating ? null : "finish");
    } else if (mode === "load") {
        menuToShow = (error.isPresent() ? "error" : "loading");
    }
    const showMenu = (menuToShow !== null);

    const [pressedStartEver, setPressedStartEver] = useState(false);

    return (
        <div className={cn(
            "relative flex flex-col items-stretch w-full min-h-72 md:min-h-104 overflow-hidden",
            "p-3 gap-2 md:gap-3 border border-solid border-primary rounded-xl bg-surface-sunken",
            className ?? null,
        )}>
            <div
                className="flex flex-row flex-wrap gap-4 w-full transition-opacity duration-300 ease-in-out"
                style={{ opacity: showMenu ? 0 : 1 }}>

                <PerfectGameControl onClick={toggleMuted}>
                    {muted ? "Unmute" : "Mute"}
                </PerfectGameControl>
                <PerfectGameControl onClick={skip}>
                    Stop
                </PerfectGameControl>
            </div>

            <BoardUI
                horizontal={true}
                isMobile={true}
                isPathActive={false}
                drawStartEndPieces={true}
                skipKeyListeners={true}
                sharedRendererState={sharedRendererState} />

            <div
                className="transition-opacity duration-300 ease-in-out"
                style={{ opacity: showMenu ? 0 : 1 }}>

                <PerfectGameMetadata
                    lwps={lwps}
                    winner={game ? game.getWinner() : undefined}
                    sharedRendererState={sharedRendererState} />
            </div>

            <div
                className={cn(
                    "absolute left-0 top-0 flex flex-col items-center justify-end w-full h-full p-4",
                    "gap-2 md:gap-0 transition-opacity duration-300 ease-in-out",
                    pressedStartEver ? "bg-scrim" : null,
                )}
                style={showMenu ? {
                    opacity: 1,
                } : {
                    opacity: 0,
                    pointerEvents: "none",
                }}>

                {menuToShow === "loading" && (
                    <p>
                        Preparing your game
                        <DotDotDot enabled={true} />
                    </p>
                )}

                {menuToShow === "finish" && lwps && winner && (
                    <>
                        <p className="m-0 font-bold text-strong text-4xl md:text-4xl">
                            {winner.getName()}
                            {" "}
                            Won
                        </p>
                        <p className="m-0 text-body text-base md:mt-1 md:mb-4">
                            Here&apos;s a graph showing light&apos;s chance of winning
                            throughout the game.
                        </p>
                        <div className="w-4/5 md:pb-4">
                            <AnalysisSnakeGraph percentages={lwps} />
                        </div>
                    </>
                )}


                {error.isPresent() && (
                    <p className="w-full p-4 text-danger text-center">
                        Unable to load game:
                        {" "}
                        {error.get().message}
                    </p>
                )}

                {(menuToShow === "start" || menuToShow === "finish" || menuToShow === "error") && (
                    <Button
                        tone="accent"
                        size={isMobile ? "md" : "lg"}
                        onClick={() => {
                            setPressedStartEver(true);
                            gameSource.error.set(Optional.empty());
                            gameSource.loadNewGame();
                        }}>

                        {menuToShow === "start" && "Start perfect game"}
                        {menuToShow === "finish" && "Start new game"}
                        {menuToShow === "error" && "Try again"}
                    </Button>
                )}
            </div>
        </div>
    );
}


interface PerfectGameProps {
    className?: string;
}


export function PerfectGame({ className }: PerfectGameProps) {
    const clientControls = useSingleton<GameClientControls>(() => {
        const missing = () => {
            throw new Error("This function is missing!");
        };
        return {
            startGame: missing,
            startingGame: false,
            startGameError: null,
            clearError: missing,
            resignGame: missing,
            abortGame: missing,
            notify: missing,
        };
    });

    const user = useOptionalUser();
    const { preferences } = useGamePreferences(user);
    const gameAPI = useGameAPI();
    const gameSource = useSingleton(() => {
        return new PerfectGameSource(
            clientControls,
            preferences,
            matomo,
            gameAPI,
        );
    });

    return (
        <DeviceInfoProvider>
            <AssetProvider>
                <GameUIContext gameSource={gameSource} clientControls={clientControls}>
                    <InternalPerfectGame
                        className={className}
                        gameSource={gameSource} />
                </GameUIContext>
            </AssetProvider>
        </DeviceInfoProvider>
    );
}
